import type {
  SessionStats,
  SearchSessionsResult,
  SessionListItem,
  Session,
  SessionTags,
} from "../types";

// Helper to generate dates for the last 30 days
function generateLast30Days(): string[] {
  const days: string[] = [];
  const today = new Date();
  for (let i = 29; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    days.push(date.toISOString().split("T")[0]);
  }
  return days;
}

// Helper to check if a date is a weekday
function isWeekday(dateStr: string): boolean {
  const date = new Date(dateStr);
  const day = date.getDay();
  return day >= 1 && day <= 5; // Monday to Friday
}

// Generate sessions by day (more on weekdays)
function generateSessionsByDay() {
  const days = generateLast30Days();
  return days.map((date) => {
    const baseCount = isWeekday(date) ? 22 : 8; // Weekdays get more sessions
    const variance = Math.floor(Math.random() * 8) - 4; // Random variance ±4
    return {
      date,
      count: Math.max(1, baseCount + variance),
    };
  });
}

// Mock data for useSessionStats
export const mockSessionStats: SessionStats = {
  total_sessions: {
    call: 517, // ~90% of 575
    chat: 35,
    email: 23,
  },
  total_minutes: {
    call: 537.18, // ~90% of 600
    chat: 35,
    email: 25,
  },
  total_messages: {
    call: 2585, // ~5 messages per call session
    chat: 280, // ~8 messages per chat session
    email: 92, // ~4 messages per email session
  },
  sessions_by_day: generateSessionsByDay(),
  // One bucket per session: these add up to the session total.
  sessions_by_tag: [
    { tag: "booking_creation", count: 180 },
    { tag: "booking_reschedule", count: 45 },
    { tag: "booking_cancellation", count: 35 },
    { tag: "transfer", count: 115 },
    { tag: "qualification_form", count: 85 },
    { tag: "other", count: 110 },
  ],
  sessions_by_result: [
    { result: "success", count: 520 },
    { result: "failure", count: 15 },
    { result: "no_answer", count: 25 },
    { result: "voicemail", count: 10 },
    { result: "unknown", count: 5 },
    { result: "pending", count: 0 },
  ],
  sessions_by_sentiment: [
    { sentiment: "positive", count: 380 },
    { sentiment: "neutral", count: 165 },
    { sentiment: "negative", count: 30 },
  ],
  unique_customers: 298,
  total_value: 18680, // in cents
  total_value_by_medium: {
    call: 16112, // ~90%
    chat: 2784,
    email: 1784,
  },
  average_duration: 63, // ~63 seconds (600 minutes / 575 sessions)
  average_duration_by_medium: {
    call: 63, // Similar to average
    chat: 60,
    email: 65,
  },
  after_hours: { calls: 142, value: 4380, total_calls: 410 },
};

// Helper to generate realistic session data
function generateMockSessions(count: number): SessionListItem[] {
  const sessions: SessionListItem[] = [];
  const now = Date.now();

  const mediums: Array<"call" | "chat" | "email"> = ["call", "chat", "email"];
  const types: Array<"inbound" | "outbound" | "notifications"> = [
    "inbound",
    "outbound",
    "notifications",
  ];
  const results: Array<
    "success" | "failure" | "no_answer" | "voicemail" | "unknown"
  > = ["success", "failure", "no_answer", "voicemail", "unknown"];
  const sentiments: Array<"positive" | "neutral" | "negative"> = [
    "positive",
    "neutral",
    "negative",
  ];
  const tags: SessionTags[] = [
    "transfer",
    "booking_creation",
    "booking_cancellation",
    "booking_reschedule",
    "qualification_form",
  ];

  const purposes = [
    "Book an appointment for next week",
    "Request information about services",
    "Cancel existing reservation",
    "Reschedule appointment to different date",
    "General inquiry about availability",
    "Confirm booking details",
    "Update contact information",
    "Request callback for consultation",
  ];

  const names = [
    "Sarah Johnson",
    "Michael Smith",
    "Emma Williams",
    "James Brown",
    "Olivia Davis",
    "William Miller",
    "Sophia Wilson",
    "Robert Moore",
  ];

  for (let i = 0; i < count; i++) {
    const timestamp = now - i * 3600000 - Math.random() * 3600000; // Spread over last few hours
    const medium =
      i === 0 || Math.random() > 0.1
        ? "call"
        : mediums[Math.floor(Math.random() * mediums.length)];
    const sessionType =
      Math.random() > 0.3
        ? "inbound"
        : types[Math.floor(Math.random() * types.length)];
    const result =
      Math.random() > 0.1
        ? "success"
        : results[Math.floor(Math.random() * results.length)];
    const sentiment =
      Math.random() > 0.7
        ? "positive"
        : sentiments[Math.floor(Math.random() * sentiments.length)];
    const duration = Math.floor(30 + Math.random() * 120); // 30-150 seconds

    // Pick 1-2 tags, mostly booking related
    const sessionTags =
      Math.random() > 0.3
        ? [tags[Math.floor(Math.random() * 4) + 1]] // a booking, or a form
        : [tags[0]]; // a transfer

    sessions.push({
      id: `session_${i + 1}`, // Stable ID without timestamp
      public_id: `PUB${String(i + 1).padStart(6, "0")}`,
      client_id: "demo_client",
      customer_id: `customer_${Math.floor(Math.random() * 50) + 1}`,
      agent_id: "agent_ai",
      type: sessionType,
      medium,
      state: "closed",
      result,
      sentiment,
      purpose: purposes[i % purposes.length],
      name: names[i % names.length],
      summary: `Session summary for ${names[i % names.length]}. ${purposes[i % purposes.length]}.`,
      tags: sessionTags,
      duration,
      context: {
        phone_number: `+1 ${String(
          Math.floor(Math.random() * 9000000000) + 1000000000,
        ).replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3")}`,
        timezone: "America/New_York",
      },
      created_at: new Date(timestamp).toISOString(),
      updated_at: new Date(timestamp).toISOString(),
    });
  }

  return sessions;
}

// Generate a stable pool of sessions once
const MOCK_SESSIONS_POOL = generateMockSessions(50);

// Mock data for useSearchSessions (for recent calls table)
export const mockSearchSessionsResult: SearchSessionsResult = {
  data: MOCK_SESSIONS_POOL.slice(0, 5),
  total: 575,
  limit: 5,
  offset: 0,
  has_more: true,
};

// Mock data for useSession (single session by ID with messages)
export function getMockSession(sessionId: string): Session {
  const sessionListItem =
    MOCK_SESSIONS_POOL.find((s) => s.id === sessionId) || MOCK_SESSIONS_POOL[0];

  // Convert SessionListItem to Session by adding messages
  const session: Session = {
    ...sessionListItem,
    messages: [
      {
        type: "user",
        value: "Hello, I'd like to book an appointment.",
        timestamp: new Date(sessionListItem.created_at).getTime(),
      },
      {
        type: "agent",
        value:
          "Hello! I'd be happy to help you book an appointment. What time works best for you?",
        timestamp: new Date(sessionListItem.created_at).getTime() + 2000,
      },
      {
        type: "user",
        value: "I'm looking for next Tuesday afternoon if possible.",
        timestamp: new Date(sessionListItem.created_at).getTime() + 5000,
      },
      {
        type: "agent",
        value:
          "Let me check our availability for next Tuesday afternoon. I have slots available at 2:00 PM and 3:30 PM. Which would you prefer?",
        timestamp: new Date(sessionListItem.created_at).getTime() + 8000,
      },
      {
        type: "user",
        value: "The 2:00 PM slot would be perfect.",
        timestamp: new Date(sessionListItem.created_at).getTime() + 12000,
      },
      {
        type: "agent",
        value:
          "Great! I've booked you for Tuesday at 2:00 PM. You should receive a confirmation email shortly. Is there anything else I can help you with?",
        timestamp: new Date(sessionListItem.created_at).getTime() + 15000,
      },
      {
        type: "user",
        value: "No, that's all. Thank you!",
        timestamp: new Date(sessionListItem.created_at).getTime() + 18000,
      },
      {
        type: "agent",
        value: "You're welcome! Have a great day and see you on Tuesday.",
        timestamp: new Date(sessionListItem.created_at).getTime() + 20000,
      },
    ],
  };

  return session;
}
